import { auth } from '../firebase/config';
import { processCitations } from '../utils/citationUtils';

/* ---------------------------------------------------------
 * Chat Query API
 * -------------------------------------------------------*/
async function processChatQuery(query, siteData, sourcePriorities = {}, conversationHistory = []) {
  if (!auth.currentUser) throw new Error('Not authenticated');
  const token = await auth.currentUser.getIdToken();
  
  const response = await fetch('https://us-central1-megalith-maps.cloudfunctions.net/chatQuery', {
    method: 'POST',
    headers: { 
      'Content-Type': 'application/json', 
      'Authorization': `Bearer ${token}` 
    },
    body: JSON.stringify({
      query,
      siteData,
      sourcePriorities,
      conversationHistory
    })
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to process chat query');
  }
  
  return await response.json();
}

/* ---------------------------------------------------------
 * Helper: Extract sections from markdown by H3 headings
 * Returns an object with section names as keys and content as values
 * -------------------------------------------------------*/
const extractSectionsFromMarkdown = (markdown) => {
  if (!markdown) return {};
  
  // Split by H3 headers (### Header)
  const sectionRegex = /### ([^\n]+)(?:\n([\s\S]*?)(?=### |$))/g;
  const sections = {};
  let match;
  
  while ((match = sectionRegex.exec(markdown)) !== null) {
    // match[1] is the header text, match[2] is the content
    const headerText = match[1].trim();
    const content = match[2].trim();
    
    // Map header text to field name, being flexible with variations
    let fieldName;
    
    // Normalize the header text for more reliable matching (remove punctuation, lowercase)
    const normalizedHeader = headerText.toLowerCase()
                                      .replace(/[\/&]/g, ' ')  // Replace / and & with spaces
                                      .replace(/\s+/g, ' ')    // Normalize spaces
                                      .trim();
    
    // Match header to field using flexible pattern matching
    if (normalizedHeader.includes('overview') || normalizedHeader.includes('introduction')) {
      fieldName = 'overviewIntroduction';
    } 
    else if (normalizedHeader.includes('discovery') || normalizedHeader.includes('excavation')) {
      fieldName = 'discoveryAndExcavation';
    } 
    else if (normalizedHeader.includes('historical context') || normalizedHeader.includes('chronology')) {
      fieldName = 'historicalContextAndChronology_narrative';
    } 
    else if (normalizedHeader.includes('architectural') || normalizedHeader.includes('features') || normalizedHeader.includes('layout')) {
      fieldName = 'keyArchitecturalFeaturesLayout';
    } 
    else if (normalizedHeader.includes('purpose') || normalizedHeader.includes('function')) {
      fieldName = 'purposeAndFunction';
    } 
    else if (normalizedHeader.includes('cultural') && normalizedHeader.includes('significance')) {
      fieldName = 'culturalSignificance_narrative';
    } 
    else if (normalizedHeader.includes('finds') || normalizedHeader.includes('artifacts')) {
      fieldName = 'notableFindsArtifacts_narrative';
    } 
    else if (normalizedHeader.includes('theories') || normalizedHeader.includes('interpretations')) {
      fieldName = 'theoriesAndInterpretations';
    } 
    else if ((normalizedHeader.includes('current') || normalizedHeader.includes('present')) && 
             (normalizedHeader.includes('status') || normalizedHeader.includes('preservation'))) {
      fieldName = 'currentStatusAndPreservation_narrative';
    }
    else {
      // If header doesn't match any known section, we'll skip it
      continue;
    }
    
    sections[fieldName] = content;
  }
  
  return sections;
};

const sanitizeRepeatedMarkers = (text) => {
  if (typeof text !== 'string' || !text) return text;
  // Regex: (\[\d+\]) captures a bracketed number (e.g., "[28]").
  // (\1+) matches one or more immediate repetitions of what the first group captured.
  // Replaces the entire matched sequence (e.g., "[28][28][28]") with just the first captured group ("$1", e.g., "[28]").
  return text.replace(/(\[\d+\])(\1+)/g, '$1');
};


/* ---------------------------------------------------------
 * Helper: combine citation & grounding metadata into a flat
 * list of article objects { id, title, url, description }
 * -------------------------------------------------------*/
export const formatGroundingArticles = (groundingMetadata, citationMetadata) => {
  const articles = [];

  // 1 – citations array (new schema)
  if (citationMetadata?.citations) {
    citationMetadata.citations.forEach((c, i) => {
      if (!c.uri) return;
      articles.push({ id:`citation-${i}`, url:c.uri, title:c.title||c.uri, description:'', type:'citation' });
    });
  }

  // 2 – legacy grounding chunks
  if (groundingMetadata?.groundingChunks) {
    groundingMetadata.groundingChunks.forEach((chunk, i) => {
      const uri = chunk.web?.uri;
      if (!uri) return;
      if (!articles.some(a => a.url === uri)) {
        articles.push({ id:`grounding-${i}`, url:uri, title:chunk.web.title||uri, description:chunk.web.snippet||'' });
      }
    });
  }

  return articles;
};

/* ---------------------------------------------------------
 * Main fetch
 * -------------------------------------------------------*/
const aiService = {
  processChatQuery,
  
  async getSiteInfo(siteName) {
    if (!auth.currentUser) throw new Error('Not authenticated');
    const token = await auth.currentUser.getIdToken();
    const resp  = await fetch('https://us-central1-megalith-maps.cloudfunctions.net/aiSiteInfo', {
      method :'POST',
      headers:{ 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
      body   : JSON.stringify({ siteName })
    });
    if (!resp.ok) throw new Error(await resp.text());
    return await resp.json();
  },

  /* ------------------------------------
   * Format result into siteData object
   * ----------------------------------*/
  formatSiteInfo(apiResult) {
    if (!apiResult?.success) return null;
    const d = apiResult.data;

    // build grounding articles list
    const groundingArticles = formatGroundingArticles(d.groundingMetadata, d.citationMetadata);
    
    // Process references (new primary source) or fall back to sources/citations
    let processedReferences = [];
    if (Array.isArray(d.references) && d.references.length) {
      // New 'references' field is present and populated
      processedReferences = d.references.map((ref, index) => ({
        id: ref.id || `[${index + 1}]`, // Should have ID from aiSiteInfo
        title: ref.title || 'Untitled Source',
        url: ref.url || '',
        type: ref.type || 'unknown',
        description: ref.description || ref.referenceText || '',
        retrievedInfo: ref.retrievedInfo || [],
        publicationDate: ref.publicationDate || null,
        referenceText: ref.referenceText || '',
        license: ref.license || null,
        isManual: ref.isManual || false, // Pass through the isManual flag
      }));
    } else if (Array.isArray(d.sources) && d.sources.length) {
      // Fallback to 'sources' if 'references' is not available
      console.warn("aiService.formatSiteInfo: Using fallback 'sources' array. Consider updating to 'references'.");
      processedReferences = processCitations(d.sources.map(source => ({ // processCitations will handle ID and type
        id: source.id, 
        title: source.title || source.referenceText || '',
        url: source.url || '',
        referenceText: source.referenceText || '',
        // Pass other fields if available, processCitations might use them
        description: source.description, 
        publicationDate: source.publicationDate,
        license: source.license,
        retrievedInfo: source.retrievedInfo
      })));
    } else if (Array.isArray(d.citations) && d.citations.length) {
      // Legacy path for older 'citations' (array of strings/objects)
      console.warn("aiService.formatSiteInfo: Using legacy 'citations' array.");
      processedReferences = processCitations(d.citations.map((cit,i)=> {
        if (typeof cit === 'string') return { id:`[${i+1}]`, title:cit, url:cit.startsWith('http')?cit:null };
        return { ...cit, id: cit.id || `[${i+1}]`}; // Ensure ID
      }));
    } else if (groundingArticles.length) {
      // Fallback to grounding articles if nothing else
      console.warn("aiService.formatSiteInfo: Falling back to groundingArticles for references.");
      processedReferences = processCitations(groundingArticles.map((a,i)=>({ id:`[${i+1}]`, title:a.title, url:a.url })));
    }
    
    // Extract individual sections from the markdown description
    // This will be our fallback if the API didn't provide individual fields
    const extractedSections = extractSectionsFromMarkdown(d.description || '');
    
    // For each section field, use the direct API value if available,
    // otherwise use our extracted value from the markdown description, then sanitize.
    let overviewIntroduction = d.overviewIntroduction || extractedSections.overviewIntroduction || '';
    overviewIntroduction = sanitizeRepeatedMarkers(overviewIntroduction);

    let discoveryAndExcavation = d.discoveryAndExcavation || extractedSections.discoveryAndExcavation || '';
    discoveryAndExcavation = sanitizeRepeatedMarkers(discoveryAndExcavation);

    let historicalContextAndChronology_narrative = d.historicalContextAndChronology_narrative || extractedSections.historicalContextAndChronology_narrative || '';
    historicalContextAndChronology_narrative = sanitizeRepeatedMarkers(historicalContextAndChronology_narrative);

    let keyArchitecturalFeaturesLayout = d.keyArchitecturalFeaturesLayout || extractedSections.keyArchitecturalFeaturesLayout || '';
    keyArchitecturalFeaturesLayout = sanitizeRepeatedMarkers(keyArchitecturalFeaturesLayout);

    let purposeAndFunction = d.purposeAndFunction || extractedSections.purposeAndFunction || '';
    purposeAndFunction = sanitizeRepeatedMarkers(purposeAndFunction);

    let culturalSignificance_narrative = d.culturalSignificance_narrative || extractedSections.culturalSignificance_narrative || '';
    culturalSignificance_narrative = sanitizeRepeatedMarkers(culturalSignificance_narrative);

    let notableFindsArtifacts_narrative = d.notableFindsArtifacts_narrative || extractedSections.notableFindsArtifacts_narrative || '';
    notableFindsArtifacts_narrative = sanitizeRepeatedMarkers(notableFindsArtifacts_narrative);

    let theoriesAndInterpretations = d.theoriesAndInterpretations || extractedSections.theoriesAndInterpretations || '';
    theoriesAndInterpretations = sanitizeRepeatedMarkers(theoriesAndInterpretations);

    let currentStatusAndPreservation_narrative = d.currentStatusAndPreservation_narrative || extractedSections.currentStatusAndPreservation_narrative || '';
    currentStatusAndPreservation_narrative = sanitizeRepeatedMarkers(currentStatusAndPreservation_narrative);
    
    let description = d.description || '';
    description = sanitizeRepeatedMarkers(description);


    return {
      name            : d.siteName || d.name || '', // Added d.name as a fallback
      description     : description, // Sanitized
      // New structured description fields - now with fallbacks from extracted markdown and sanitized
      overviewIntroduction, // Sanitized
      discoveryAndExcavation, // Sanitized
      historicalContextAndChronology_narrative, // Sanitized
      keyArchitecturalFeaturesLayout, // Sanitized
      purposeAndFunction, // Sanitized
      culturalSignificance_narrative, // Sanitized
      notableFindsArtifacts_narrative, // Sanitized
      theoriesAndInterpretations, // Sanitized
      currentStatusAndPreservation_narrative, // Sanitized
      // Existing fields
      era             : d.era || '',
      culture         : Array.isArray(d.culture) ? d.culture : (d.culture ? [d.culture] : []), // Ensure culture is an array
      constructionPeriods: Array.isArray(d.constructionPeriods) 
        ? d.constructionPeriods.map(period => ({
            ...period,
            details: period.details || period.description || period.text || period.content || '',
          }))
        : [],
      keyDiscoveries  : d.keyDiscoveries || [], // Added keyDiscoveries
      historicalSignificance: d.historicalSignificance || '', // Added historicalSignificance
      currentStatus   : d.currentStatus || '', // Added currentStatus
      features        : d.features || [], // Added features
      // sources         : d.sources || [], // No longer pass raw sources, use processedReferences
      location        : d.location || {},
      references      : processedReferences, // Use the new processed references array
      // citations       : citations, // Remove old citations field
      groundingMetadata: d.groundingMetadata, // Keep for now, might be used by other parts or for debugging
      citationMetadata : d.citationMetadata,  // Keep for now
      groundingArticles: groundingArticles, // Keep for now
      modelInfo       : d.modelInfo,
      spellCheck      : d.spellCheck // Ensure spellCheck data is passed through
    };
  }
};

export default aiService;
